import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MapPin, Users, Building2, Wifi, Music, Utensils, Car, Shield,
  X, ChevronLeft, ChevronRight, ArrowRight, Check, Sun, Waves
} from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

// Lightbox Component
const Lightbox = ({ images, currentIndex, onClose, onNext, onPrev }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'ArrowLeft') onPrev();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose, onNext, onPrev]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
      onClick={onClose}
    >
      <button onClick={onClose} className="absolute top-6 right-6 text-white/60 hover:text-white z-10">
        <X size={32} />
      </button>
      <button onClick={(e) => { e.stopPropagation(); onPrev(); }} className="absolute left-6 text-white/60 hover:text-white z-10">
        <ChevronLeft size={48} />
      </button>
      <button onClick={(e) => { e.stopPropagation(); onNext(); }} className="absolute right-6 text-white/60 hover:text-white z-10">
        <ChevronRight size={48} />
      </button>
      <img
        src={images[currentIndex]}
        alt=""
        className="max-h-[90vh] max-w-[90vw] object-contain"
        onClick={(e) => e.stopPropagation()}
      />
      <div className="absolute bottom-6 text-white/60 text-sm">
        {currentIndex + 1} / {images.length}
      </div>
    </motion.div>
  );
};

export default function VenueDetailPage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [relatedVenues, setRelatedVenues] = useState([]);

  useEffect(() => {
    const fetchVenue = async () => {
      try {
        const response = await axios.get(`${API}/public/venues/${slug}`);
        setVenue(response.data);
        
        const relatedResponse = await axios.get(`${API}/public/venues?limit=4`);
        setRelatedVenues(relatedResponse.data.items?.filter(v => v.slug !== slug).slice(0, 3) || []);
      } catch (error) {
        console.error('Error fetching venue:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchVenue();
  }, [slug]);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const nextImage = () => {
    if (venue?.gallery?.length) {
      setLightboxIndex((prev) => (prev + 1) % venue.gallery.length);
    }
  };

  const prevImage = () => {
    if (venue?.gallery?.length) {
      setLightboxIndex((prev) => (prev - 1 + venue.gallery.length) % venue.gallery.length);
    }
  };

  const amenityIcons = {
    'DJ Booth': Music,
    'Sound System': Music,
    'Parking': Car,
    'Catering': Utensils,
    'WiFi': Wifi,
    'Security': Shield,
    'Stage': Building2,
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-12 h-12 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!venue) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-display text-4xl text-white mb-4">Venue no encontrado</h1>
            <Link to="/venues" className="btn-gold">Ver Espacios</Link>
          </div>
        </div>
      </Layout>
    );
  }

  const galleryImages = venue.gallery?.length > 0 
    ? venue.gallery 
    : [venue.cover_image || 'https://images.unsplash.com/photo-1566417713940-fe7c737a9ef2?w=800&q=80'];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-end" data-testid="venue-hero">
        <div className="absolute inset-0">
          <img
            src={venue.cover_image || 'https://images.unsplash.com/photo-1566417713940-fe7c737a9ef2?w=1920&q=80'}
            alt={venue.name?.[lang] || venue.name?.es}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
        </div>

        <div className="container-premium relative pb-16 pt-32">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
          >
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-2 mb-4">
              {venue.venue_type?.map((type) => (
                <span key={type} className="px-3 py-1 bg-[#C0C0C0]/20 text-[#C0C0C0] text-xs rounded-full uppercase tracking-wider">
                  {type}
                </span>
              ))}
              {venue.rooftop && (
                <span className="px-3 py-1 bg-[#D4AF37]/20 text-[#D4AF37] text-xs rounded-full uppercase tracking-wider flex items-center gap-1">
                  <Sun size={12} /> Rooftop
                </span>
              )}
              {venue.beach && (
                <span className="px-3 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full uppercase tracking-wider flex items-center gap-1">
                  <Waves size={12} /> Beach
                </span>
              )}
            </motion.div>
            
            <motion.h1 variants={fadeInUp} className="font-display text-5xl md:text-7xl lg:text-8xl text-white mb-4">
              {venue.name?.[lang] || venue.name?.es}
            </motion.h1>
            
            <motion.div variants={fadeInUp} className="flex flex-wrap items-center gap-6 text-white/60">
              <span className="flex items-center gap-2">
                <MapPin size={18} className="text-[#D4AF37]" />
                {venue.city}, {venue.country}
              </span>
              <span className="flex items-center gap-2">
                <Users size={18} className="text-[#D4AF37]" />
                {lang === 'es' ? 'Capacidad' : 'Capacity'}: {venue.capacity?.toLocaleString()}
              </span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="section-dark py-20" data-testid="venue-content">
        <div className="container-premium">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-16">
              {/* Description */}
              {(venue.description?.es || venue.description?.en) && (
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                >
                  <h2 className="font-display text-3xl text-white mb-6">
                    {lang === 'es' ? 'Sobre el Espacio' : 'About the Venue'}
                  </h2>
                  <p className="text-white/70 leading-relaxed text-lg whitespace-pre-line">
                    {venue.description?.[lang] || venue.description?.es}
                  </p>
                </motion.div>
              )}

              {/* Features & Amenities */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
              >
                <h2 className="font-display text-3xl text-white mb-6">
                  {lang === 'es' ? 'Características' : 'Features'}
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {venue.features?.map((feature, index) => (
                    <div key={index} className="flex items-center gap-3 p-4 glass-dark rounded-xl">
                      <Check size={18} className="text-[#D4AF37]" />
                      <span className="text-white/80">{feature}</span>
                    </div>
                  ))}
                  {venue.amenities?.map((amenity, index) => {
                    const Icon = amenityIcons[amenity] || Check;
                    return (
                      <div key={`a-${index}`} className="flex items-center gap-3 p-4 glass-dark rounded-xl">
                        <Icon size={18} className="text-[#C0C0C0]" />
                        <span className="text-white/80">{amenity}</span>
                      </div>
                    );
                  })}
                </div>
              </motion.div>

              {/* Gallery */}
              {galleryImages.length > 0 && (
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                >
                  <h2 className="font-display text-3xl text-white mb-6">
                    {lang === 'es' ? 'Galería' : 'Gallery'}
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {galleryImages.map((image, index) => (
                      <div
                        key={index}
                        className={`overflow-hidden cursor-pointer group ${index === 0 ? 'col-span-2 row-span-2' : ''}`}
                        onClick={() => openLightbox(index)}
                      >
                        <img
                          src={image}
                          alt={`${venue.name?.[lang]} - ${index + 1}`}
                          className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 ${index === 0 ? 'aspect-[4/3]' : 'aspect-square'}`}
                        />
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Technical Info */}
              {(venue.technical_info?.es || venue.technical_info?.en) && (
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                >
                  <h2 className="font-display text-3xl text-white mb-6">
                    {lang === 'es' ? 'Información Técnica' : 'Technical Information'}
                  </h2>
                  <div className="glass-dark rounded-xl p-6">
                    <p className="text-white/70 whitespace-pre-line">
                      {venue.technical_info?.[lang] || venue.technical_info?.es}
                    </p>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* Request CTA */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                className="glass-dark rounded-2xl p-8 sticky top-32"
              >
                <h3 className="font-display text-2xl text-white mb-4">
                  {lang === 'es' ? 'Solicitar Información' : 'Request Information'}
                </h3>
                <p className="text-white/60 mb-6">
                  {lang === 'es' 
                    ? '¿Interesado en este espacio para tu evento?'
                    : 'Interested in this venue for your event?'
                  }
                </p>
                <Link
                  to={`/contact?venue=${slug}`}
                  className="btn-gold w-full text-center block mb-4"
                  data-testid="venue-cta"
                >
                  {t('venues.request_info')}
                </Link>
                <Link
                  to="/contact"
                  className="btn-secondary w-full text-center block"
                >
                  {lang === 'es' ? 'Producir Evento Aquí' : 'Produce Event Here'}
                </Link>
              </motion.div>

              {/* Quick Info */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                className="glass-dark rounded-2xl p-8"
              >
                <h4 className="text-white/50 text-xs uppercase tracking-wider mb-4">
                  {lang === 'es' ? 'Detalles' : 'Details'}
                </h4>
                <ul className="space-y-4">
                  <li className="flex justify-between">
                    <span className="text-white/50">{lang === 'es' ? 'Ubicación' : 'Location'}</span>
                    <span className="text-white">{venue.city}</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-white/50">{lang === 'es' ? 'Capacidad' : 'Capacity'}</span>
                    <span className="text-white">{venue.capacity?.toLocaleString()}</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-white/50">{lang === 'es' ? 'Interior' : 'Indoor'}</span>
                    <span className="text-white">{venue.indoor ? '✓' : '—'}</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-white/50">{lang === 'es' ? 'Exterior' : 'Outdoor'}</span>
                    <span className="text-white">{venue.outdoor ? '✓' : '—'}</span>
                  </li>
                </ul>
              </motion.div>

              {/* Address */}
              {venue.address && (
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                  className="glass-dark rounded-2xl p-8"
                >
                  <h4 className="text-white/50 text-xs uppercase tracking-wider mb-4">
                    {lang === 'es' ? 'Dirección' : 'Address'}
                  </h4>
                  <p className="text-white">{venue.address}</p>
                  <p className="text-white/60">{venue.city}, {venue.country}</p>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Related Venues */}
      {relatedVenues.length > 0 && (
        <section className="section-subtle py-20" data-testid="related-venues">
          <div className="container-premium">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
            >
              <motion.div variants={fadeInUp} className="flex items-end justify-between mb-12">
                <h2 className="font-display text-3xl text-white">
                  {lang === 'es' ? 'Otros Espacios' : 'Other Venues'}
                </h2>
                <Link to="/venues" className="text-white/60 hover:text-white flex items-center gap-2">
                  {t('venues.view_all')}
                  <ArrowRight size={16} />
                </Link>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedVenues.map((relatedVenue) => (
                  <motion.div key={relatedVenue.slug} variants={fadeInUp}>
                    <Link to={`/venues/${relatedVenue.slug}`} className="block group">
                      <div className="premium-card overflow-hidden">
                        <div className="relative aspect-[4/3] overflow-hidden">
                          <img
                            src={relatedVenue.cover_image || 'https://images.unsplash.com/photo-1566417713940-fe7c737a9ef2?w=600&q=80'}
                            alt={relatedVenue.name?.[lang] || relatedVenue.name?.es}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          />
                        </div>
                        <div className="p-6">
                          <p className="text-[#C0C0C0] text-xs tracking-wider uppercase mb-2">
                            {relatedVenue.venue_type?.[0]} • {relatedVenue.city}
                          </p>
                          <h3 className="font-display text-xl text-white mb-2">
                            {relatedVenue.name?.[lang] || relatedVenue.name?.es}
                          </h3>
                          <p className="text-white/50 text-sm">
                            {t('venues.capacity')}: {relatedVenue.capacity?.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && (
          <Lightbox
            images={galleryImages}
            currentIndex={lightboxIndex}
            onClose={() => setLightboxOpen(false)}
            onNext={nextImage}
            onPrev={prevImage}
          />
        )}
      </AnimatePresence>
    </Layout>
  );
}
